import React, { Component, ErrorInfo, ReactNode } from 'react';

import { reportClientError } from '../../base/reportClientError';

import ComponentErrorFallback from './ComponentErrorFallback';

type Props = Readonly<{
    /** Component identifier used to label the report (e.g. the lazy path). */
    component?: string;
    children: ReactNode;
}>;

type State = Readonly<{ hasError: boolean; code: string | null }>;

/**
 * Error boundary that, on a render failure, reports the error to the backend
 * (getting a correlation code) and shows a fallback that surfaces that code to
 * the user. Replaces the previous Sentry-only boundary, which logged nowhere
 * once Sentry was disabled.
 */
class ClientErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, code: null };
    }

    static getDerivedStateFromError(): Partial<State> {
        // Stop rendering children immediately so we don't re-throw on next render.
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        const { component } = this.props;
        const code = reportClientError(error, {
            kind: 'react-render',
            component,
            componentStack: info.componentStack ?? undefined,
        });
        this.setState({ code });
    }

    render(): ReactNode {
        const { hasError, code } = this.state;
        const { children } = this.props;

        return hasError ? <ComponentErrorFallback code={code} /> : children;
    }
}

export default ClientErrorBoundary;
